export type AnchorBox = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export type MenuSize = { width: number; height: number };

export type ViewportSize = { width: number; height: number };

export type MenuPosition = { left: number; top: number; maxHeight?: number };

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 4;

export const placeAnchoredPopover = (anchor: AnchorBox, popover: MenuSize, viewport: ViewportSize): MenuPosition => {
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left + anchor.width / 2 - popover.width / 2, viewport.width - popover.width - VIEWPORT_MARGIN));

  const below = anchor.bottom + ANCHOR_GAP;
  if (below + popover.height <= viewport.height - VIEWPORT_MARGIN) return { left, top: below };

  const bottomEdge = anchor.top - ANCHOR_GAP;
  const top = bottomEdge - popover.height;
  if (top >= VIEWPORT_MARGIN) return { left, top };
  const roomAbove = bottomEdge - VIEWPORT_MARGIN;
  const roomBelow = viewport.height - VIEWPORT_MARGIN - below;
  if (roomBelow >= roomAbove) return { left, maxHeight: roomBelow, top: below };
  return { left, maxHeight: roomAbove, top: VIEWPORT_MARGIN };
};

export type MenuAlignment = { align: 'center' | 'start'; direction: 'ltr' | 'rtl' };

const CENTERED: MenuAlignment = { align: 'center', direction: 'ltr' };

export const placeAnchoredMenu = (anchor: AnchorBox, menu: MenuSize, viewport: ViewportSize, alignment: MenuAlignment = CENTERED): MenuPosition => {
  const clampLeft = (left: number): number => Math.max(VIEWPORT_MARGIN, Math.min(left, viewport.width - menu.width - VIEWPORT_MARGIN));
  const startLeft = alignment.direction === 'rtl' ? anchor.right - menu.width : anchor.left;

  const below = anchor.bottom + ANCHOR_GAP;
  const belowLeft = clampLeft(alignment.align === 'start' ? startLeft : anchor.left + anchor.width / 2 - menu.width / 2);
  if (below + menu.height <= viewport.height - VIEWPORT_MARGIN) {
    return { left: belowLeft, top: below };
  }

  const aboveLeft = clampLeft(alignment.align === 'start' ? startLeft : anchor.right);
  const bottomEdge = anchor.top - ANCHOR_GAP;
  const top = bottomEdge - menu.height;
  if (top >= VIEWPORT_MARGIN) {
    return { left: aboveLeft, top };
  }
  const roomAbove = bottomEdge - VIEWPORT_MARGIN;
  const roomBelow = viewport.height - VIEWPORT_MARGIN - below;
  if (roomBelow >= roomAbove) return { left: belowLeft, maxHeight: roomBelow, top: below };
  return { left: aboveLeft, maxHeight: roomAbove, top: VIEWPORT_MARGIN };
};

/**
 * A submenu opens beside its parent row, not below it.
 *
 * It leads to the inline-end side and flips to the inline-start when there is no
 * room, which is the convention every desktop menu follows: a submenu that
 * dropped below its row would be indistinguishable from the parent menu's own
 * next item. Vertically it aligns to the row's top and is nudged up only as far
 * as needed to stay on screen, so the row and the first submenu item line up
 * whenever they can.
 */
export const placeSubmenu = (anchor: AnchorBox, menu: MenuSize, viewport: ViewportSize): MenuPosition => {
  // Overlap the parent's padding by a hair: a visible gap between a row and its
  // submenu lets the pointer fall through the crack and dismiss it on the way
  // across.
  const beside = anchor.right - ANCHOR_GAP;
  const flipped = anchor.left - menu.width + ANCHOR_GAP;
  const fitsEnd = beside + menu.width <= viewport.width - VIEWPORT_MARGIN;
  const left = fitsEnd ? beside : Math.max(VIEWPORT_MARGIN, flipped);

  const maxHeight = viewport.height - VIEWPORT_MARGIN * 2;
  const height = Math.min(menu.height, maxHeight);
  const top = Math.max(VIEWPORT_MARGIN, Math.min(anchor.top, viewport.height - height - VIEWPORT_MARGIN));
  return { left, maxHeight: menu.height > maxHeight ? maxHeight : undefined, top };
};
